import React, { memo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Share2 } from 'lucide-react';
import { MetaTags } from '../../../../components/SEO/MetaTags';
import { RelatedBlogPosts } from '../../../../components/Blog/RelatedBlogPosts';
export function BlogPostNarcissismGaslightingES() {
  const articleData = {
    title: 'Aprende los términos: Narcisismo y Gaslighting',
    description: 'Aprende sobre narcisismo y gaslighting. Entiende las tácticas de manipulación y cómo la IA detecta patrones tóxicos en textos.',
    slug: 'narcissism-gaslighting-explained',
    publishDate: '2025-07-15',
    author: 'Toxic Detector Editorial'
  };
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: articleData.title,
    description: articleData.description,
    datePublished: articleData.publishDate,
    author: {
      '@type': 'Organization',
      name: 'ToxicDetector'
    },
    publisher: {
      '@type': 'Organization',
      name: 'ToxicDetector'
    }
  };
  const handleShare = async () => {
    try {
      await navigator.share({
        title: articleData.title,
        text: articleData.description,
        url: 'https://toxicdetector.com/'
      });
    } catch (err) {
      navigator.clipboard.writeText(window.location.href);
      alert('¡Enlace copiado al portapapeles!');
    }
  };
  return <>
      <MetaTags title="Narcisismo y Gaslighting explicados | ToxicDetector" description="Entiende la psicología detrás de las tácticas de manipulación de narcisismo y gaslighting." keywords="narcisismo, gaslighting, manipulación, psicología, análisis IA" canonical={`https://toxicdetector.com/es/blog/${articleData.slug}`} ogType="article" ogImage="https://cdn.magicpatterns.com/uploads/iLMUMmEFFWiRSqv8DSTAdz/file_00000000f0c8720a9c4e7f9e2fbdea6d.png" schema={[articleSchema]} data-id="element-5308" />

      <div className="flex flex-col w-full min-h-screen bg-white" data-id="element-5309">
        <header className="bg-white/95 backdrop-blur-md border-b border-gray-100 sticky top-0 z-50" data-id="element-5310">
          <div className="max-w-4xl mx-auto px-6 py-5" data-id="element-5311">
            <div className="flex items-center justify-between" data-id="element-5312">
              <Link to="/" className="flex items-center gap-2 group" data-id="element-5313">
                <span className="text-2xl" data-id="element-5314">🚩</span>
                <span className="text-xl font-bold text-gray-900 group-hover:text-red-600 transition-colors" data-id="element-5315">
                  ToxicDetector
                </span>
              </Link>
              <button onClick={handleShare} className="p-2.5 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-5316">
                <Share2 size={20} className="text-gray-600" data-id="element-5317" />
              </button>
            </div>
          </div>
        </header>

        <article className="max-w-4xl mx-auto w-full px-6 py-16" data-id="element-5318">
          <Link to="/" className="inline-flex items-center text-gray-600 text-sm mb-12 hover:text-gray-900 transition-colors group" data-id="element-5319">
            <ArrowLeft size={16} className="mr-2 group-hover:-translate-x-1 transition-transform" data-id="element-5320" />
            Volver al Inicio
          </Link>

          <header className="mb-16" data-id="element-5321">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 tracking-tight leading-[1.1] text-gray-900" data-id="element-5322">
              {articleData.title}
            </h1>
            <div className="flex items-center text-gray-500 text-sm gap-3" data-id="element-5323">
              <time dateTime={articleData.publishDate} data-id="element-5324">
                {new Date(articleData.publishDate).toLocaleDateString('es-MX', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
              </time>
              <span className="text-gray-300" data-id="element-5325">•</span>
              <span data-id="element-5326">{articleData.author}</span>
            </div>
          </header>

          <div className="prose prose-lg max-w-none" data-id="element-5327">
            <p className="text-2xl mb-10 leading-relaxed text-gray-700 font-medium" data-id="element-5328">
              Narcisismo y gaslighting son términos que se usan a menudo, pero
              ¿qué significan realmente? ¿Y cómo están conectados?
            </p>

            <section className="mb-16" data-id="element-5329">
              <h2 className="text-4xl font-bold mb-8 tracking-tight text-gray-900" data-id="element-5330">
                ¿Qué es el Narcisismo?
              </h2>
              <p className="text-lg leading-relaxed text-gray-600" data-id="element-5331">
                El narcisismo es más que vanidad. Es un rasgo de personalidad
                caracterizado por un sentido inflado de importancia propia, una
                profunda necesidad de admiración y falta de empatía.
              </p>
            </section>

            <section className="mb-16" data-id="element-5332">
              <h2 className="text-4xl font-bold mb-8 tracking-tight text-gray-900" data-id="element-5333">
                ¿Qué es el Gaslighting?
              </h2>
              <p className="text-lg leading-relaxed text-gray-600" data-id="element-5334">
                El gaslighting es una forma de manipulación psicológica en la
                que una persona siembra dudas en la percepción, memoria o juicio
                de otra persona.
              </p>
            </section>

            <section className="mb-16" data-id="element-5335">
              <h2 className="text-4xl font-bold mb-8 tracking-tight text-gray-900" data-id="element-5336">
                La Conexión
              </h2>
              <p className="text-lg leading-relaxed text-gray-600" data-id="element-5337">
                Los narcisistas a menudo usan el gaslighting como una
                herramienta para mantener el control y proteger su autoimagen.
                Es una combinación peligrosa.
              </p>
            </section>
          </div>

          <div className="bg-purple-600 rounded-2xl p-12 md:p-16 text-center text-white shadow-xl mt-16" data-id="element-5338">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 tracking-tight leading-tight" data-id="element-5339">
              Reconoce las señales.
            </h2>
            <p className="text-purple-100 mb-10 text-xl leading-relaxed max-w-2xl mx-auto" data-id="element-5340">
              No te dejes manipular. Usa la IA para obtener claridad.
            </p>
            <Link to="/upload" className="inline-block bg-white text-purple-600 px-12 py-5 rounded-xl font-bold text-lg hover:bg-gray-50 transition-all shadow-lg hover:shadow-xl hover:scale-105" data-id="element-5341">
              Escanear ahora →
            </Link>
          </div>

          <div className="mt-20" data-id="element-5342">
            <RelatedBlogPosts posts={[{
            title: '7 Señales de Gaslighting',
            description: 'Descubre 7 señales de gaslighting que nuestra IA detecta al instante.',
            slug: '7-signs-of-gaslighting',
            emoji: '💀'
          }, {
            title: '¿Es tu ex un narcisista encubierto?',
            description: 'Descubre si tu ex muestra señales de comportamiento narcisista encubierto.',
            slug: 'is-your-ex-a-covert-narcissist',
            emoji: '🎭'
          }]} data-id="element-5343" />
          </div>
        </article>

        <footer className="bg-gray-900 text-white py-16 mt-24" data-id="element-5344">
          <div className="max-w-4xl mx-auto px-6 text-center" data-id="element-5345">
            <p className="text-gray-400 text-sm" data-id="element-5346">
              © {new Date().getFullYear()} ToxicDetector. Todos los derechos
              reservados.
            </p>
          </div>
        </footer>
      </div>
    </>;
}